import {
  createValidationResult,
  findObjectForPropertyInModelContext,
  IModelContext,
  IValidationResult,
  ModelItem,
  ValidationFunction
} from '@mng/model';
import {
  dateToIsoDateString,
  getBeginningOfNextMonth,
  getBeginningOfNextMonthAsIsoDate,
  getBeginningOfThisMonth,
  getBeginningOfThisMonthAsIsoDate,
  isoDateStringToDate,
  today
} from '@mng/mng-ts-helpers';
import { addMonths, isAfter, isBefore } from 'date-fns';
import {
  ITEM_GV_PERSONEN_DATA_PERSONEN_GV_TARIF_DATA_TARIFE_TARIF_INFO,
  ITEM_GV_PERSONEN_DATA_PERSONEN_STAMMDATEN,
  ITEM_MODIFIED_DATE
} from '../gvangebot.model.paths';
import { GvAngebotData, GvPerson, GvTarif } from '../gvangebot.model.types';
import { isFzKontoDatumInExpiryRange } from './logic/gvangebot-model.logic';
import { GvRisikoType, hasGvAngebotTarife } from './logic/gvtarif-model.logic';

export const MAX_MONTHS_IN_FUTURE = 11;
export const DEFAULT_RUECKDATIERUNG_STICHTAG = 22;
export const DEFAULT_EXTRA_WEEKS = 4;
export const DEFAULT_STICHDATUM_MAX_OFFSET = 8;
// Diese FZ-Konten-Liste is auch in MIS zu Warten
export const ERWEITERTE_RUECKDATIERUNG_NEUGEBORENE_FZ_KONTEN = ['3837', '4006', '4011', '4012'];

export function isVersicherungsBeginnOverSixMonthsInFuture(
  versicherungsBeginn: string,
  now: Date = new Date()
): boolean {
  const vb = isoDateStringToDate(versicherungsBeginn);
  const sixMonthsFromNow = addMonths(now, 6);
  return vb > sixMonthsFromNow;
}

export function gvVersicherungsbeginnValidation(modelItem: ModelItem, modelContext: IModelContext): IValidationResult {
  const tarif: GvTarif = findObjectForPropertyInModelContext(
    modelContext,
    ITEM_GV_PERSONEN_DATA_PERSONEN_GV_TARIF_DATA_TARIFE_TARIF_INFO
  );
  const person: GvPerson = findObjectForPropertyInModelContext(modelContext, ITEM_GV_PERSONEN_DATA_PERSONEN_STAMMDATEN);
  const gvAngebotData: GvAngebotData = findObjectForPropertyInModelContext(modelContext, ITEM_MODIFIED_DATE);

  return gvValidateVersicherungsbeginn(
    modelContext.value,
    tarif,
    person,
    gvAngebotData,
    isoDateStringToDate(person.stammdaten.gebdatum)
  );
}

export function gvValidateVersicherungsbeginn(
  versBeginn: string,
  tarif: GvTarif,
  person: GvPerson,
  gvAngebotData: GvAngebotData,
  geburtstag: Date
): IValidationResult {
  if (tarif.risikoTyp === GvRisikoType.BESTAND) {
    // bei bestandstarifen wird der Versicherungsbeginn nicht validiert
    return null;
  }

  let tarifVersionStichtag: Date;
  let rueckdatierungsStichtag: number;

  if (gvAngebotData) {
    if (gvAngebotData.tarifVersionStichtag) {
      tarifVersionStichtag = isoDateStringToDate(gvAngebotData.tarifVersionStichtag);
    } else if (gvAngebotData.modifiedDate) {
      tarifVersionStichtag = isoDateStringToDate(gvAngebotData.modifiedDate);
    } else {
      tarifVersionStichtag = getBeginningOfNextMonth();
    }
    rueckdatierungsStichtag =
      gvAngebotData.config && gvAngebotData.config.stichtagVersBeginn
        ? gvAngebotData.config.stichtagVersBeginn
        : DEFAULT_RUECKDATIERUNG_STICHTAG;
  } else {
    console.error('gvAngebotData nicht gefunden');
  }

  const erlaubteDaten = gvErlaubteVersicherungsBeginnDatumsWerte(
    rueckdatierungsStichtag,
    tarifVersionStichtag,
    geburtstag,
    tarif.fzKonto?.konto,
    isoDateStringToDate(gvAngebotData.vertragsAenderungsDatum)
  );
  const valid = !versBeginn || erlaubteDaten.includes(versBeginn);
  return valid
    ? null
    : createValidationResult(
        'versicherungsBeginn',
        null,
        'Ungültiger Versicherungsbeginn! Wählen Sie ein neues Datum.'
      );
}

export function gvComputeFruehestesVersicherungsBeginnDatum(
  relationDate: Date,
  rueckdatierungsStichtag: number,
  geburtstag: Date = new Date(0),
  fzKontoNr?: string,
  now = today()
): Date {
  let fruehesterBeginn: Date;
  if (relationDate.getDate() <= rueckdatierungsStichtag) {
    fruehesterBeginn = getBeginningOfThisMonth(relationDate);
  } else {
    fruehesterBeginn = getBeginningOfNextMonth(relationDate);
  }
  const erweiterteRueckdatierung = ERWEITERTE_RUECKDATIERUNG_NEUGEBORENE_FZ_KONTEN.includes(fzKontoNr);
  // Spezialbehandlung von Neugeborenen
  if (now <= addMonths(geburtstag, erweiterteRueckdatierung ? 2 : 1)) {
    fruehesterBeginn.setMonth(geburtstag.getMonth());
    fruehesterBeginn.setFullYear(geburtstag.getFullYear());
  }
  return fruehesterBeginn;
}

export function getDefaultVersicherungsBeginn(relationDate: Date, rueckdatierungsStichtag: number): string {
  const fruehesterBeginn = gvComputeFruehestesVersicherungsBeginnDatum(relationDate, rueckdatierungsStichtag);
  return getBeginningOfThisMonthAsIsoDate(fruehesterBeginn);
}

export function getDefaultTarifVersionStichtag(rueckdatierungsStichtag: number): string {
  if (today().getDate() <= rueckdatierungsStichtag) {
    return getBeginningOfThisMonthAsIsoDate();
  } else {
    return getBeginningOfNextMonthAsIsoDate();
  }
}

export function getDefaultVertragsAenderungsDatum(tarifVersionStichtag: Date, rueckdatierungsStichtag: number): string {
  const fruehesterBeginn = gvComputeFruehestesVersicherungsBeginnDatum(tarifVersionStichtag, rueckdatierungsStichtag);
  return getBeginningOfThisMonthAsIsoDate(fruehesterBeginn);
}

export function getErlaubteVertragsAenderungsDati(
  tarifVersionStichtag: Date,
  gvRueckdatierungStichtag: number
): string[] {
  return gvErlaubteVersicherungsBeginnDatumsWerte(gvRueckdatierungStichtag, tarifVersionStichtag);
}

export function gvErlaubteVersicherungsBeginnDatumsWerte(
  rueckdatierungsStichtag: number,
  tarifVersionStichtag: Date,
  geburtstag: Date = new Date(0),
  fzKontoNr?: string,
  vertragsAenderungsDatum?: Date,
  now: Date = today()
): string[] {
  const dates: string[] = [];

  const relationDate = isAfter(tarifVersionStichtag, now) ? now : tarifVersionStichtag;

  const maxInFuture = addMonths(relationDate, MAX_MONTHS_IN_FUTURE);

  let versBeginnRelationDate = tarifVersionStichtag;

  if (vertragsAenderungsDatum && isAfter(vertragsAenderungsDatum, relationDate)) {
    versBeginnRelationDate = vertragsAenderungsDatum;
  }

  const fruehesterBeginn = gvComputeFruehestesVersicherungsBeginnDatum(
    versBeginnRelationDate,
    rueckdatierungsStichtag,
    geburtstag,
    fzKontoNr,
    now
  );

  // MAX_MONTHS_IN_FUTURE + 3 da erlaubte VB in der Zukunft nicht immer relativ zu fruehesterBeginn errechnet werden (zB. Rückdatierung auf 1. des Geburtsmonats)
  for (let i = 0; i <= MAX_MONTHS_IN_FUTURE + 3; i++) {
    const dateCandidate = addMonths(fruehesterBeginn, i);

    if (dateCandidate <= maxInFuture) {
      dates.push(dateToIsoDateString(dateCandidate));
    }
  }

  return dates;
}

export const gvTarifeVorhandenValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const gvAngebotData: GvAngebotData = modelContext.value;
  if (!hasGvAngebotTarife(gvAngebotData.gvPersonenData)) {
    return createValidationResult('gvAngebot', null, 'Es muss zumindest 1 Tarif vorhanden sein');
  }
  return null;
};

export const gvMustRecalculateValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const gvAngebotData: GvAngebotData = modelContext.value;
  if (gvAngebotData.mustRecalculate) {
    return createValidationResult('gvAngebot', null, 'Das Angebot muss neu berechnet werden');
  }
  return null;
};

export const gvFzKontoExpiryValidationFunction: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const fzDatum = modelContext.value;
  const gvAngebotData = <GvAngebotData>modelContext.dataStack[0];
  if (!isFzKontoDatumInExpiryRange(fzDatum, gvAngebotData)) {
    return createValidationResult(
      'gvFzKontoExpired',
      null,
      'Das angegebene Vertrags-Änderungsdatum liegt nicht im Gültigkeitsbereich des FZ-Kontos'
    );
  }
  return null;
};

export const gvVnChangedValidationFunction: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const aenderungsDatum = modelContext.value;
  if (!aenderungsDatum) {
    return createValidationResult(
      'gvVertragsAenderungsDatumRequired',
      null,
      'Die Angabe eines Vertrags-Änderungsdatum ist bei VN-Wechsel verpflichtend'
    );
  }
  return null;
};

export const vertragsAenderungAfterTarifVersionStichtagValidationFunction: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const aenderungsDatum = modelContext.value;
  const gvAngebotData = <GvAngebotData>modelContext.dataStack[0];
  if (isBefore(isoDateStringToDate(aenderungsDatum), isoDateStringToDate(gvAngebotData.tarifVersionStichtag))) {
    return createValidationResult(
      'gvVertragsAenderungsDatumBeforeTarifVersionStichtag',
      null,
      'Das Vertrags-Änderungsdatum darf nicht vor dem Tarif-Version-Stichtag sein'
    );
  }
  return null;
};
